import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Plus, 
  Search, 
  User, 
  Calendar, 
  Target, 
  TrendingUp, 
  Trash2, 
  ChevronDown, 
  ChevronRight,
  LayoutGrid,
  List,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Clock,
  ArrowUpRight
} from 'lucide-react';
import Navbar from '../components/Navbar';
import AddPatientDialog from '../components/AddPatientDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { patientsAPI, adminAPI } from '../services/api';
import { formatNumber } from '../utils/numberFormat';
import { calculateNutritionalStatus } from '../utils/nutritionalStatus';
import StatusDot from '../components/StatusDot';
import Footer from '../components/Footer';
import AccessBanner from '../components/AccessBanner';
import BackToTop from '../components/BackToTop';

export default function PatientList({ user: propUser }) {
  const navigate = useNavigate();

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'on_track', 'warning', 'critical', 'no_records'
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [user, setUser] = useState(propUser || null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, patient: null });
  const [allUsers, setAllUsers] = useState([]); // For admin to group patients by user
  const [expandedUsers, setExpandedUsers] = useState({}); // Track which user rows are expanded

  // Helper to get max patients based on role
  const getMaxPatients = () => {
    if (!user) return 60; // Default
    switch (user.role) {
      case 'trial':
        return 10;
      case 'premium':
        return 60;
      case 'admin':
        return Infinity; // Unlimited
      default:
        return 10;
    }
  };

  useEffect(() => {
    // Get user info from prop or token
    if (propUser) {
      setUser(propUser);
    } else {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setUser(payload);
        } catch (e) {
          console.error('Invalid token');
        }
      }
    }
    
    loadPatients();
  }, [propUser]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      
      // For admin, load users first before patients
      if (user?.role === 'admin') {
        try {
          const userData = await adminAPI.getAllUsers();
          const usersList = userData.users || userData || [];
          setAllUsers(usersList);
        } catch (error) {
          console.error('Failed to load users:', error);
          setAllUsers([]);
        }
      }
      
      // Then load patients
      const data = await patientsAPI.getAll();
      setPatients(data);
    } catch (error) {
      console.error('Failed to load patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPatient = async (patientData) => {
    const maxPatients = getMaxPatients();
    if (user?.role !== 'admin' && patients.length >= maxPatients) {
      const roleName = user?.role === 'trial' ? 'Trial' : user?.role === 'premium' ? 'Premium' : 'your plan';
      alert(`Patient Limit Reached\n\nYou have reached the maximum limit of ${maxPatients} patients for ${roleName}. Please delete some patients before adding new ones.`);
      return;
    }

    try {
      await patientsAPI.create(patientData);
      setShowAddDialog(false);
      loadPatients();
    } catch (error) {
      console.error('Failed to create patient:', error);
      alert('Failed to create patient');
    }
  };

  const handleDeletePatient = async () => {
    if (!deleteConfirm.patient) return;
    
    try {
      await patientsAPI.delete(deleteConfirm.patient.id);
      setDeleteConfirm({ open: false, patient: null });
      loadPatients();
    } catch (error) {
      console.error('Failed to delete patient:', error);
      alert('Failed to delete patient');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  // Status classifier helper
  const getPatientStatusCategory = (patient) => {
    if (!patient.latest_record_date || !patient.latest_intake || !patient.latest_target) {
      return 'no_records';
    }
    const pct = (patient.latest_intake / patient.latest_target) * 100;
    if (pct >= 80 && pct <= 120) return 'on_track';
    if ((pct >= 50 && pct < 80) || (pct > 120 && pct <= 150)) return 'warning';
    return 'critical';
  };

  // Counts for status chips
  const counts = {
    all: patients.length,
    on_track: patients.filter(p => getPatientStatusCategory(p) === 'on_track').length,
    warning: patients.filter(p => getPatientStatusCategory(p) === 'warning').length,
    critical: patients.filter(p => getPatientStatusCategory(p) === 'critical').length,
    no_records: patients.filter(p => getPatientStatusCategory(p) === 'no_records').length,
  };

  // Filtered patients by search and status
  const filteredPatients = patients.filter(patient => {
    const matchesSearch = patient.name.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (statusFilter === 'all') return true;
    return getPatientStatusCategory(patient) === statusFilter;
  });

  // Group patients by user for admin view
  const groupedPatients = user?.role === 'admin' ? filteredPatients.reduce((groups, patient) => {
    const userId = patient.user_id || 'unknown';
    if (!groups[userId]) {
      groups[userId] = [];
    }
    groups[userId].push(patient);
    return groups;
  }, {}) : null;

  // Get user info by ID
  const getUserInfo = (userId) => {
    if (!Array.isArray(allUsers)) return { name: 'Unknown User', email: 'unknown@example.com' };
    const userInfo = allUsers.find(u => u.id === userId);
    return userInfo || { name: 'Deleted User', email: 'deleted@nutrimus.com' };
  };

  // Toggle user row expansion
  const toggleUserExpansion = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const getPatientRouteBadge = (patient) => {
    const route = patient?.latest_route;
    if (!route) return null;

    if (route === 'enteral_parenteral') {
      return (
        <span className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 border border-purple-200" title="Rute Asupan Terkini: Kombinasi Enteral + Parenteral">
          <span>🥣+💉</span> Enteral + PN
        </span>
      );
    }
    if (route === 'enteral') {
      return (
        <span className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-200" title="Rute Asupan Terkini: Formula Enteral / Sonde">
          <span>🥣</span> Enteral
        </span>
      );
    }
    if (route === 'parenteral') {
      return (
        <span className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 border border-purple-200" title="Rute Asupan Terkini: Nutrisi Parenteral / Infus">
          <span>💉</span> Parenteral
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200" title="Rute Asupan Terkini: Oral">
        <span>🥗</span> Oral
      </span>
    );
  };

  const getStatusBadge = (patient) => {
    if (!patient.latest_record_date) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
          <Clock className="w-3 h-3 text-slate-400" /> Belum Ada Data
        </span>
      );
    }

    const daysSinceLastRecord = Math.floor(
      (new Date() - new Date(patient.latest_record_date)) / (1000 * 60 * 60 * 24)
    );

    if (daysSinceLastRecord > 7) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
          <Clock className="w-3 h-3 text-slate-400" /> Tidak Aktif (&gt;7 hr)
        </span>
      );
    }

    if (!patient.latest_intake || !patient.latest_target) {
      return <span className="text-xs text-slate-400">-</span>;
    }

    const percentage = (patient.latest_intake / patient.latest_target) * 100;

    if (percentage > 120) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200" title="Asupan melebihi 120% target kebutuhan harian">
          <AlertTriangle className="w-3 h-3 text-orange-600" /> Waspada Overfeeding ({Math.round(percentage)}%)
        </span>
      );
    } else if (percentage >= 80 && percentage <= 120) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" title="Asupan nutrisi adekuat memenuhi target kebutuhan">
          <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Target Tercapai ({Math.round(percentage)}%)
        </span>
      );
    } else if (percentage >= 50 && percentage < 80) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" title="Defisit nutrisi ringan hingga sedang (50-79%)">
          <AlertTriangle className="w-3 h-3 text-amber-600" /> Defisit Ringan ({Math.round(percentage)}%)
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200" title="Asupan di bawah 50% target. Risiko tinggi malnutrisi">
          <AlertCircle className="w-3 h-3 text-rose-600" /> Defisit Berat / Malnutrisi ({Math.round(percentage)}%)
        </span>
      );
    }
  };

  const formatLastActivity = (date) => {
    if (!date) return 'Belum ada recall';
    
    const lastDate = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now - lastDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    
    if (diffDays === 0) {
      if (diffHours === 0) return 'Hari ini';
      return `${diffHours} jam lalu`;
    } else if (diffDays === 1) {
      return 'Kemarin';
    } else if (diffDays < 7) {
      return `${diffDays} hari lalu`;
    } else {
      return lastDate.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col justify-between">
      <div>
        {/* Top Navbar */}
        <Navbar 
          user={user} 
          onLogout={handleLogout} 
          breadcrumbs={[{ label: 'Daftar Pasien' }]}
        />

        {/* Access Status Banner */}
        <AccessBanner user={user} />

        {/* Main Content Area */}
        <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3.5 sm:py-8 space-y-3.5 sm:space-y-6">
          {/* Header Row: Title, Limits, & Add Patient */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold tracking-tight text-slate-900">
                Manajemen Pasien
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1">
                Kelola data pasien rawat inap dan lakukan recall nutrisi harian.
                {user?.role !== 'admin' && (
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] sm:text-xs font-medium bg-slate-100 text-slate-700">
                    Kuota: {patients.length} / {getMaxPatients()} Pasien
                  </span>
                )}
              </p>
            </div>
            
            {user?.role !== 'admin' && (
              <Button 
                onClick={() => setShowAddDialog(true)} 
                className="w-full sm:w-auto h-9 sm:h-10 text-xs sm:text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-sm transition-all"
                disabled={patients.length >= getMaxPatients()}
              >
                <Plus className="h-4 w-4 mr-1.5 sm:mr-2" />
                Tambah Pasien Baru
              </Button>
            )}
          </div>

          {/* Limit Warnings */}
          {user?.role !== 'admin' && patients.length >= (getMaxPatients() - Math.ceil(getMaxPatients() * 0.2)) && patients.length < getMaxPatients() && (
            <div className="p-3 sm:p-3.5 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2.5 text-amber-800 text-xs sm:text-sm">
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 flex-shrink-0" />
              <span>
                Mendekati Batas Kuota: Tersisa <strong>{getMaxPatients() - patients.length} slot pasien</strong> lagi pada paket Anda.
              </span>
            </div>
          )}

          {user?.role !== 'admin' && patients.length >= getMaxPatients() && (
            <div className="p-3 sm:p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2.5 text-red-800 text-xs sm:text-sm">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 flex-shrink-0" />
              <span>
                Batas Pasien Tercapai: Anda telah menggunakan seluruh <strong>{getMaxPatients()} slot pasien</strong>. Hapus beberapa pasien lama untuk menambahkan yang baru.
              </span>
            </div>
          )}

          {/* Quick Stats Metric Cards */}
          {patients.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
              <Card className="border border-slate-200/80 bg-white shadow-xs">
                <CardContent className="p-2.5 sm:p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-[11px] sm:text-xs font-medium text-slate-500 truncate">Total Pasien</p>
                    <p className="text-lg sm:text-2xl font-bold text-slate-900 mt-0.5 sm:mt-1">{patients.length}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 flex-shrink-0">
                    <User className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-emerald-100 bg-emerald-50/40 shadow-xs">
                <CardContent className="p-2.5 sm:p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-[11px] sm:text-xs font-medium text-emerald-700 truncate">
                      Sesuai Target <span className="hidden sm:inline">(≥80%)</span>
                    </p>
                    <p className="text-lg sm:text-2xl font-bold text-emerald-800 mt-0.5 sm:mt-1">{counts.on_track}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700 flex-shrink-0">
                    <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-amber-100 bg-amber-50/40 shadow-xs">
                <CardContent className="p-2.5 sm:p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-[11px] sm:text-xs font-medium text-amber-700 truncate">
                      Perhatian <span className="hidden sm:inline">(50-79%)</span>
                    </p>
                    <p className="text-lg sm:text-2xl font-bold text-amber-800 mt-0.5 sm:mt-1">{counts.warning}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-amber-100 flex items-center justify-center text-amber-700 flex-shrink-0">
                    <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-red-100 bg-red-50/40 shadow-xs">
                <CardContent className="p-2.5 sm:p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-[11px] sm:text-xs font-medium text-red-700 truncate">
                      Kritis <span className="hidden sm:inline">(&lt;50% / &gt;150%)</span>
                    </p>
                    <p className="text-lg sm:text-2xl font-bold text-red-800 mt-0.5 sm:mt-1">{counts.critical}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-red-100 flex items-center justify-center text-red-700 flex-shrink-0">
                    <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Search, Filter Chips, and View Mode Bar */}
          <div className="bg-white p-2.5 sm:p-4 rounded-xl border border-slate-200 shadow-xs space-y-2.5 sm:space-y-3">
            {/* Top Row: Search Input + View Mode Toggle Inline */}
            <div className="flex items-center justify-between gap-2 sm:gap-3">
              {/* Search Bar */}
              <div className="relative flex-1 min-w-0 md:max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Cari nama pasien..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 sm:pl-10 h-9 sm:h-10 text-xs sm:text-sm bg-slate-50/60 border-slate-200 focus:bg-white transition-colors w-full"
                />
              </div>

              {/* View Mode Toggle for Non-Admin */}
              {user?.role !== 'admin' && (
                <div className="flex items-center gap-0.5 sm:gap-1 bg-slate-100 p-0.5 sm:p-1 rounded-lg border border-slate-200 flex-shrink-0">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-md text-[11px] sm:text-xs font-medium transition-all ${
                      viewMode === 'grid'
                        ? 'bg-white text-slate-800 shadow-xs'
                        : 'text-slate-500 hover:text-slate-900'
                    }`}
                    title="Tampilan Grid Kartu"
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                    <span>Kartu</span>
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-md text-[11px] sm:text-xs font-medium transition-all ${
                      viewMode === 'table'
                        ? 'bg-white text-slate-800 shadow-xs'
                        : 'text-slate-500 hover:text-slate-900'
                    }`}
                    title="Tampilan Tabel Sensus Bangsal"
                  >
                    <List className="h-3.5 w-3.5" />
                    <span>Tabel</span>
                  </button>
                </div>
              )}
            </div>

            {/* Quick Status Chips */}
            {user?.role !== 'admin' && patients.length > 0 && (
              <div className="pt-2 border-t border-slate-100">
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs">
                  <button
                    onClick={() => setStatusFilter('all')}
                    className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full font-medium transition-all flex items-center gap-1.5 ${
                      statusFilter === 'all'
                        ? 'bg-slate-900 text-white shadow-xs'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <span>Semua</span>
                    <span className="text-[10px] opacity-75 font-semibold">({counts.all})</span>
                  </button>
                  <button
                    onClick={() => setStatusFilter('on_track')}
                    className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full font-medium transition-all flex items-center gap-1.5 ${
                      statusFilter === 'on_track'
                        ? 'bg-emerald-600 text-white shadow-xs'
                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                    <span>Sesuai <span className="hidden sm:inline">Target</span></span>
                    <span className="text-[10px] opacity-75 font-semibold">({counts.on_track})</span>
                  </button>
                  <button
                    onClick={() => setStatusFilter('warning')}
                    className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full font-medium transition-all flex items-center gap-1.5 ${
                      statusFilter === 'warning'
                        ? 'bg-amber-600 text-white shadow-xs'
                        : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0"></span>
                    <span>Perhatian</span>
                    <span className="text-[10px] opacity-75 font-semibold">({counts.warning})</span>
                  </button>
                  <button
                    onClick={() => setStatusFilter('critical')}
                    className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full font-medium transition-all flex items-center gap-1.5 ${
                      statusFilter === 'critical'
                        ? 'bg-red-600 text-white shadow-xs'
                        : 'bg-red-50 text-red-700 hover:bg-red-100'
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"></span>
                    <span>Kritis</span>
                    <span className="text-[10px] opacity-75 font-semibold">({counts.critical})</span>
                  </button>
                  <button
                    onClick={() => setStatusFilter('no_records')}
                    className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full font-medium transition-all flex items-center gap-1.5 ${
                      statusFilter === 'no_records'
                        ? 'bg-slate-600 text-white shadow-xs'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <span>Belum Recall</span>
                    <span className="text-[10px] opacity-75 font-semibold">({counts.no_records})</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Patient Listing Content */}
          {loading || (user?.role === 'admin' && allUsers.length === 0) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 shadow-xs space-y-3 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex-shrink-0" />
                      <div className="space-y-1.5">
                        <div className="h-4 w-28 bg-slate-200 rounded" />
                        <div className="h-3 w-16 bg-slate-100 rounded" />
                      </div>
                    </div>
                    <div className="h-5 w-14 bg-slate-100 rounded-full" />
                  </div>
                  <div className="pt-2 border-t border-slate-100 space-y-2">
                    <div className="flex justify-between">
                      <div className="h-3 w-20 bg-slate-100 rounded" />
                      <div className="h-3 w-12 bg-slate-200 rounded" />
                    </div>
                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-slate-200 rounded-full w-3/4" />
                    </div>
                  </div>
                  <div className="pt-1 flex gap-2">
                    <div className="h-8 flex-1 bg-slate-100 rounded-lg" />
                    <div className="h-8 flex-1 bg-slate-200 rounded-lg" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPatients.length === 0 ? (
            <Card className="bg-white border-dashed border-2 border-slate-200">
              <CardContent className="py-14 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
                  <User className="h-6 w-6" />
                </div>
                <h3 className="text-base font-semibold text-slate-800 mb-1">
                  {searchQuery || statusFilter !== 'all' ? 'Tidak ada pasien yang sesuai filter' : 'Belum ada pasien terdaftar'}
                </h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto mb-4">
                  {searchQuery || statusFilter !== 'all' 
                    ? 'Coba atur ulang kata kunci pencarian atau ganti filter status gizi.' 
                    : 'Mulai dengan menambahkan data pasien pertama Anda untuk mencatat recall nutrisi.'}
                </p>
                {!searchQuery && statusFilter === 'all' && user?.role !== 'admin' && (
                  <Button onClick={() => setShowAddDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 text-xs">
                    <Plus className="h-4 w-4 mr-1.5" />
                    Tambah Pasien
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : user?.role === 'admin' ? (
            /* Admin View: Grouped User Table for Desktop & Interactive Accordion Cards for Mobile */
            <div className="bg-white rounded-xl shadow-xs border border-slate-200 overflow-hidden">
              {/* Mobile View (md:hidden): Collapsible User Cards */}
              <div className="md:hidden divide-y divide-slate-100">
                {Object.entries(groupedPatients).map(([userId, userPatients]) => {
                  const userInfo = getUserInfo(userId);
                  const isExpanded = expandedUsers[userId];
                  const onTrackCount = userPatients.filter(p => p.latest_intake && p.latest_target && (p.latest_intake / p.latest_target) * 100 >= 80).length;
                  const criticalCount = userPatients.filter(p => p.latest_intake && p.latest_target && (p.latest_intake / p.latest_target) * 100 < 50).length;
                  
                  return (
                    <div key={userId} className="p-4 transition-colors">
                      {/* User Header */}
                      <div 
                        onClick={() => toggleUserExpansion(userId)}
                        className="flex items-center justify-between cursor-pointer active:bg-slate-50 -m-1 p-1 rounded-lg"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                            {userInfo.name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-sm font-semibold text-slate-900 truncate">{userInfo.name || 'Unknown'}</h4>
                            <p className="text-xs text-slate-500 truncate">{userInfo.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                            {userPatients.length} Pasien
                          </span>
                          <div className="p-1 text-slate-400">
                            {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                          </div>
                        </div>
                      </div>

                      {/* User Nutrition Quick Stats on Mobile */}
                      <div className="mt-2.5 flex items-center gap-2 text-xs text-slate-500 pl-1">
                        <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                          {onTrackCount} on track
                        </span>
                        <span className="text-slate-300">•</span>
                        <span className="inline-flex items-center gap-1 text-red-500 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                          {criticalCount} critical
                        </span>
                      </div>

                      {/* Collapsible Mobile Patient List */}
                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-slate-100 space-y-2.5">
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                            Daftar Pasien ({userPatients.length})
                          </p>
                          {userPatients.map((patient) => {
                            const hasIntake = patient.latest_intake && patient.latest_target;
                            const percentage = hasIntake ? Math.round((patient.latest_intake / patient.latest_target) * 100) : 0;

                            return (
                              <div 
                                key={patient.id}
                                onClick={() => navigate(`/patients/${patient.id}/calculate`)}
                                className="p-3 bg-slate-50/80 hover:bg-emerald-50/50 rounded-xl border border-slate-200/80 transition-colors cursor-pointer"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <h5 className="font-semibold text-sm text-slate-900">{patient.name}</h5>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                      {patient.age || '-'} th • {patient.gender === 'male' ? 'Laki-laki' : 'Perempuan'}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                    <button
                                      onClick={() => setDeleteConfirm({ open: true, patient })}
                                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                      title="Hapus Pasien"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>

                                {/* Asupan & Target Bar */}
                                <div className="mt-2 text-xs">
                                  {hasIntake ? (
                                    <div className="space-y-1">
                                      <div className="flex justify-between text-slate-700">
                                        <span>Asupan: <strong>{formatNumber(patient.latest_intake)}</strong> / {patient.latest_target} Kcal</span>
                                        <span className={`font-semibold ${
                                          percentage >= 80 && percentage <= 120 ? 'text-emerald-600' :
                                          percentage >= 50 ? 'text-amber-600' : 'text-red-600'
                                        }`}>
                                          {percentage}%
                                        </span>
                                      </div>
                                      <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                                        <div 
                                          className={`h-full rounded-full ${
                                            percentage >= 80 && percentage <= 120 ? 'bg-emerald-500' :
                                            percentage >= 50 ? 'bg-amber-500' : 'bg-red-500'
                                          }`}
                                          style={{ width: `${Math.min(percentage, 100)}%` }}
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 italic">Belum ada intake</span>
                                  )}
                                </div>

                                <div className="mt-2.5 pt-2 border-t border-slate-200/60 flex items-center justify-between text-xs text-slate-500">
                                  <span>Recall: {formatLastActivity(patient.latest_record_date)}</span>
                                  <span className="text-emerald-700 font-medium flex items-center gap-1">
                                    Buka Recall <ArrowUpRight className="h-3 w-3" />
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Desktop View (hidden md:block): Grouped User Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="w-12 px-4 py-3"></th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Jumlah Pasien</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Status Gizi Pasien</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {Object.entries(groupedPatients).map(([userId, userPatients]) => {
                      const userInfo = getUserInfo(userId);
                      const isExpanded = expandedUsers[userId];
                      
                      return (
                        <React.Fragment key={userId}>
                          <tr 
                            className="hover:bg-slate-50/70 cursor-pointer transition-colors"
                            onClick={() => toggleUserExpansion(userId)}
                          >
                            <td className="px-4 py-4 text-center">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-slate-400" />
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs">
                                  {userInfo.name?.charAt(0).toUpperCase() || 'U'}
                                </div>
                                <span className="text-sm font-semibold text-slate-800">{userInfo.name || 'Unknown'}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{userInfo.email}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                                {userPatients.length} Pasien
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-xs text-slate-500">
                              <span className="text-emerald-600 font-medium">{userPatients.filter(p => p.latest_intake && p.latest_target && (p.latest_intake / p.latest_target) * 100 >= 80).length} on track</span>
                              <span className="mx-2 text-slate-300">•</span>
                              <span className="text-red-500 font-medium">{userPatients.filter(p => p.latest_intake && p.latest_target && (p.latest_intake / p.latest_target) * 100 < 50).length} critical</span>
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr>
                              <td colSpan="5" className="px-4 py-3 bg-slate-50/60">
                                <div className="ml-10 rounded-lg border border-slate-200 bg-white overflow-hidden shadow-xs">
                                  <table className="min-w-full divide-y divide-slate-100 text-xs">
                                    <thead className="bg-slate-100/70 text-slate-600">
                                      <tr>
                                        <th className="px-4 py-2.5 text-left font-semibold">Nama Pasien</th>
                                        <th className="px-4 py-2.5 text-left font-semibold">Usia / Gender</th>
                                        <th className="px-4 py-2.5 text-left font-semibold">Recall Terakhir</th>
                                        <th className="px-4 py-2.5 text-left font-semibold">Asupan vs Target</th>
                                        <th className="px-4 py-2.5 text-center font-semibold">Aksi</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {userPatients.map((patient) => (
                                        <tr 
                                          key={patient.id} 
                                          onClick={() => navigate(`/patients/${patient.id}/calculate`)}
                                          className="hover:bg-emerald-50/40 cursor-pointer"
                                        >
                                          <td className="px-4 py-2.5 font-medium text-slate-900">{patient.name}</td>
                                          <td className="px-4 py-2.5 text-slate-500">{patient.age || '-'} th • {patient.gender === 'male' ? 'L' : 'P'}</td>
                                          <td className="px-4 py-2.5 text-slate-500">{formatLastActivity(patient.latest_record_date)}</td>
                                          <td className="px-4 py-2.5">
                                            {patient.latest_intake ? (
                                              <span className="font-semibold text-slate-800">
                                                {formatNumber(patient.latest_intake)} / {patient.latest_target} Kcal ({Math.round((patient.latest_intake / patient.latest_target) * 100)}%)
                                              </span>
                                            ) : '-'}
                                          </td>
                                          <td className="px-4 py-2.5 text-center">
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setDeleteConfirm({ open: true, patient });
                                              }}
                                              className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                              title="Hapus Pasien"
                                            >
                                              <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : viewMode === 'table' ? (
            /* Clinician Table View (Ward Census Style) */
            <div className="bg-white rounded-xl shadow-xs border border-slate-200 overflow-hidden">
              {/* Mobile Scroll Helper */}
              <div className="md:hidden px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs text-slate-500 flex items-center justify-between">
                <span className="font-medium text-slate-700">Tabel Sensus Bangsal</span>
                <span className="text-emerald-700 font-semibold flex items-center gap-1">
                  Geser ke kanan ↔
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px] divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50/80 text-xs text-slate-600 uppercase font-semibold">
                    <tr>
                      <th className="px-5 py-3.5 text-left">Nama Pasien</th>
                      <th className="px-4 py-3.5 text-left">Usia &amp; Gender</th>
                      <th className="px-4 py-3.5 text-left">Recall Terakhir</th>
                      <th className="px-5 py-3.5 text-left">Pencapaian Kalori</th>
                      <th className="px-4 py-3.5 text-left">Status Gizi</th>
                      <th className="px-5 py-3.5 text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {filteredPatients.map((patient) => {
                      const hasIntake = patient.latest_intake && patient.latest_target;
                      const percentage = hasIntake ? Math.round((patient.latest_intake / patient.latest_target) * 100) : 0;
                      
                      return (
                        <tr 
                          key={patient.id} 
                          onClick={() => navigate(`/patients/${patient.id}/calculate`)}
                          className="hover:bg-slate-50/80 cursor-pointer transition-colors group"
                        >
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-xs flex-shrink-0 group-hover:bg-emerald-100 transition-colors">
                                {patient.name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-900 group-hover:text-emerald-700 transition-colors">
                                  {patient.name}
                                </span>
                                <div className="text-xs text-slate-400">
                                  {patient.total_records || 0} catatan recall
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3.5 text-xs text-slate-600">
                            <div>{patient.age ? `${patient.age} tahun` : '-'}</div>
                            <div className="text-slate-400">{patient.gender === 'male' ? 'Laki-laki' : patient.gender === 'female' ? 'Perempuan' : '-'}</div>
                          </td>
                          <td className="px-4 py-3.5 text-xs text-slate-600">
                            {formatLastActivity(patient.latest_record_date)}
                          </td>
                          <td className="px-5 py-3.5">
                            {hasIntake ? (
                              <div className="space-y-1.5 w-44">
                                <div className="flex justify-between text-xs font-semibold text-slate-800">
                                  <span>{formatNumber(patient.latest_intake)} Kcal</span>
                                  <span className="text-slate-500 font-normal">/ {patient.latest_target}</span>
                                </div>
                                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full transition-all duration-300 ${
                                      percentage >= 80 && percentage <= 120 
                                        ? 'bg-emerald-500' 
                                        : percentage >= 50 && percentage < 80 
                                          ? 'bg-amber-500' 
                                          : 'bg-red-500'
                                    }`}
                                    style={{ width: `${Math.min(percentage, 100)}%` }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">Belum ada intake</span>
                            )}
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex flex-col gap-1 items-start">
                              {getStatusBadge(patient)}
                              {getPatientRouteBadge(patient)}
                            </div>
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/patients/${patient.id}/profile`)}
                                className="h-8 px-2 text-xs font-medium text-purple-600 hover:bg-purple-50"
                              >
                                <User className="h-3 w-3 mr-1" /> Profil
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/patients/${patient.id}/calculate`)}
                                className="h-8 px-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                              >
                                Recall <ArrowUpRight className="h-3 w-3 ml-1" />
                              </Button>
                              <button
                                onClick={() => setDeleteConfirm({ open: true, patient })}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                title="Hapus Pasien"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Clinician Grid View (Modern Cards) */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-4">
              {filteredPatients.map((patient) => {
                const hasIntake = patient.latest_intake && patient.latest_target;
                const percentage = hasIntake ? Math.round((patient.latest_intake / patient.latest_target) * 100) : 0;

                return (
                  <Card 
                    key={patient.id} 
                    onClick={() => navigate(`/patients/${patient.id}/calculate`)}
                    className="border border-slate-200/80 hover:border-emerald-300 hover:shadow-md transition-all duration-200 bg-white cursor-pointer group flex flex-col justify-between"
                  >
                    <div>
                      {/* Card Header */}
                      <div className="p-3 sm:p-4 sm:pb-3 pb-2">
                        <div className="flex items-start justify-between gap-2.5">
                          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                            <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-sm sm:text-base flex-shrink-0 group-hover:bg-emerald-100 transition-colors">
                              {patient.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-semibold text-sm sm:text-base text-slate-900 group-hover:text-emerald-700 transition-colors truncate">
                                {patient.name}
                              </h3>
                              <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5">
                                {patient.age ? `${patient.age} th` : '-'} • {patient.gender === 'male' ? 'Laki-laki' : patient.gender === 'female' ? 'Perempuan' : '-'}
                              </p>
                            </div>
                          </div>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm({ open: true, patient });
                            }}
                            className="p-1 sm:p-1.5 rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Hapus Pasien"
                          >
                            <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Card Body Info */}
                      <div className="px-3 sm:px-4 py-1 sm:py-2 space-y-2 sm:space-y-2.5">
                        {/* Intake Progress */}
                        {hasIntake ? (
                          <div className="p-2 sm:p-2.5 bg-slate-50 rounded-lg sm:rounded-xl space-y-1.5 border border-slate-100">
                            <div className="flex items-center justify-between text-[11px] sm:text-xs">
                              <span className="text-slate-500 font-medium">
                                Asupan<span className="hidden sm:inline"> Terakhir</span>:
                              </span>
                              <span className="font-bold text-slate-900">
                                {formatNumber(patient.latest_intake)}{' '}
                                <span className="font-normal text-slate-500 text-[10px] sm:text-xs">
                                  / {patient.latest_target} Kcal
                                </span>
                              </span>
                            </div>
                            <div className="w-full bg-slate-200/80 rounded-full h-1.5 sm:h-2 overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all duration-300 ${
                                  percentage >= 80 && percentage <= 120 
                                    ? 'bg-emerald-500' 
                                    : percentage >= 50 && percentage < 80 
                                      ? 'bg-amber-500' 
                                      : 'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(percentage, 100)}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="p-2 sm:p-2.5 bg-slate-50/60 rounded-lg sm:rounded-xl text-[11px] sm:text-xs text-slate-400 text-center border border-dashed border-slate-200">
                            Belum ada riwayat intake
                          </div>
                        )}

                        {/* Timing and Record Count */}
                        <div className="flex items-center justify-between text-[10px] sm:text-xs text-slate-400 px-0.5">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-slate-400" />
                            <span>{formatLastActivity(patient.latest_record_date)}</span>
                          </div>
                          <span>{patient.total_records || 0} recall</span>
                        </div>
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="mt-1 px-3 sm:px-4 py-2 sm:py-2.5 border-t border-slate-100 flex items-center justify-between bg-slate-50/40 rounded-b-xl">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {getStatusBadge(patient)}
                        {getPatientRouteBadge(patient)}
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/patients/${patient.id}/profile`); }}
                          className="text-[11px] sm:text-xs font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 px-2 py-1 rounded-lg transition-colors flex items-center gap-1"
                          title="Profil Ringkasan Pasien"
                        >
                          <User className="h-3 w-3" />
                          <span>Profil</span>
                        </button>
                        <span className="text-[11px] sm:text-xs font-medium text-emerald-700 group-hover:translate-x-0.5 transition-transform flex items-center gap-1">
                          Buka <ArrowUpRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                        </span>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Add Patient Dialog */}
      <AddPatientDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onAdd={handleAddPatient}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        type="danger"
        title="Hapus Data Pasien"
        message={`Apakah Anda yakin ingin menghapus data "${deleteConfirm.patient?.name}"? Seluruh riwayat perhitungan recall untuk pasien ini juga akan terhapus secara permanen.`}
        confirmText="Hapus Pasien"
        cancelText="Batal"
        onConfirm={handleDeletePatient}
        onClose={() => setDeleteConfirm({ open: false, patient: null })}
        onCancel={() => setDeleteConfirm({ open: false, patient: null })}
      />
      
      <Footer />
      <BackToTop />
    </div>
  );
}
